
import moment from "moment";
import { mapActions, mapGetters } from "vuex";
import { Money } from "v-money";
import { MONEY as settingMoney, SistemaMixin } from "@/modules/Application";
import { FEATURES } from "@/core/constants/features";

export default {
  name: "app-vendedor-comissoes",
  mixins: [SistemaMixin],
  components: {
    Money,
  },
  data() {
    const inicioMes = moment().startOf("month").format("YYYY-MM-DD");
    const fimMes = moment().endOf("month").format("YYYY-MM-DD");

    return {
      loading: true,
      saving: false,
      processing: false,
      tabIndex: 0,
      configMoney: settingMoney,
      plano: {
        vendedores: [],
        faixas: [],
        tabelas_comissao: [],
        formas_pagamento: [],
      },
      vendedores: [],
      formasPagamento: [],
      resultado: null,
      historico: {
        agrupamento: "semana",
        periodos: [],
      },
      agrupamentoHistorico: "auto",
      apuracoes: [],
      apuracaoSelecionada: null,
      pdfSellerId: null,
      sellerDetailsOpen: {},
      competenciaApuracao: moment().format("YYYY-MM"),
      periodo: {
        data_inicial: inicioMes,
        data_final: fimMes,
        id_vendedor: null,
      },
      bonusTypeOptions: [
        { value: null, text: "Sem bônus" },
        { value: "fixo", text: "Valor fixo" },
        {
          value: "percentual_comissao",
          text: "% sobre a comissão base",
        },
        {
          value: "pontos_percentuais",
          text: "Pontos % sobre as vendas",
        },
      ],
      paymentModeOptions: [
        { value: "vista", text: "À vista" },
        { value: "prazo", text: "A prazo" },
        { value: "ignorar", text: "Não comissionar" },
      ],
      periodShortcuts: [
        { value: "mes_atual", text: "Este mês" },
        { value: "90_dias", text: "90 dias" },
        { value: "6_meses", text: "6 meses" },
        { value: "12_meses", text: "12 meses" },
      ],
      historyGroupingOptions: [
        { value: "auto", text: "Automático" },
        { value: "semana", text: "Semanas" },
        { value: "mes", text: "Meses" },
      ],
    };
  },
  computed: {
    ...mapGetters(["itsblocked"]),
    hasModuloServicos() {
      return this.$store.getters.hasFeature(FEATURES.MOD_SERVICOS);
    },
    isEditable() {
      return true;
    },
    podeFecharApuracao() {
      return this.plano.status === "ativo";
    },
    podeCancelarApuracao() {
      if (
        !this.apuracaoSelecionada ||
        !["calculado", "aprovado"].includes(this.apuracaoSelecionada.status)
      ) {
        return false;
      }

      return (
        Number(this.resumoFinanceiroApuracao.valor_liberado || 0) <= 0 &&
        !(this.apuracaoSelecionada.vendedores || []).some(
          (vendedor) =>
            vendedor.id_conta_pagar ||
            (vendedor.liberacoes || []).some(
              (liberacao) => liberacao.id_conta_pagar
            )
        )
      );
    },
    primaryBrandColor() {
      if (typeof window === "undefined") return "#ee7361";

      return (
        window
          .getComputedStyle(document.documentElement)
          .getPropertyValue("--primary-color")
          .trim() || "#ee7361"
      );
    },
    sellerOptions() {
      return [
        { value: null, text: "Toda a equipe" },
        ...this.vendedores.map((vendedor) => ({
          value: vendedor.id_vendedor,
          text: vendedor.nome,
        })),
      ];
    },
    tabelaComissaoOptions() {
      return [
        {
          value: null,
          text: "Padrão à vista e a prazo",
        },
        ...(this.plano.tabelas_comissao || []).map((tabela) => ({
          value: tabela._key,
          text: tabela.nome || "Tabela sem nome",
        })),
      ];
    },
    formasPagamentoComClassificacao() {
      return this.plano.formas_pagamento || [];
    },
    eventoCompetenciaLabel() {
      return this.plano.evento_competencia === "recebimento"
        ? "Financeiro após recebimento"
        : "Financeiro no fechamento";
    },
    resultadoPorRecebimento() {
      return Boolean(
        this.resultado &&
          this.resultado.plano &&
          this.resultado.plano.evento_competencia === "recebimento"
      );
    },
    apuracaoPorRecebimento() {
      return Boolean(
        this.apuracaoSelecionada &&
          this.apuracaoSelecionada.plano_snapshot &&
          this.apuracaoSelecionada.plano_snapshot.evento_competencia ===
            "recebimento"
      );
    },
    resumoFinanceiroApuracao() {
      const vazio = {
        condicionado_recebimento: false,
        valor_calculado: 0,
        valor_gerado: 0,
        valor_a_pagar: 0,
        valor_disponivel: 0,
        valor_aguardando_recebimento: 0,
        valor_liberado: 0,
        saldo_liberar: 0,
        saldo_total: 0,
      };
      if (!this.apuracaoSelecionada) return vazio;

      return {
        ...vazio,
        valor_calculado: Number(this.apuracaoSelecionada.total_pagar || 0),
        valor_gerado: Number(this.apuracaoSelecionada.total_pagar || 0),
        ...(this.apuracaoSelecionada.resumo_financeiro || {}),
      };
    },
    resumoVisivel() {
      if (!this.resultado) return null;
      if (!this.periodo.id_vendedor) return this.resultado.equipe;

      const vendedor = (this.resultado.vendedores || [])[0];
      if (!vendedor) {
        return {
          meta: null,
          realizado: 0,
          atingimento: 0,
          total_comissao_base: 0,
          total_bonus: 0,
          total_ajustes: 0,
          total_pagar: 0,
          resumo_financeiro: {
            valor_disponivel: 0,
            valor_aguardando_recebimento: 0,
          },
        };
      }

      return {
        meta: vendedor.meta_individual,
        realizado: vendedor.realizado_individual,
        atingimento: vendedor.atingimento_individual,
        total_comissao_base: vendedor.comissao_base,
        total_bonus:
          Number(vendedor.bonus_individual || 0) +
          Number(vendedor.bonus_equipe || 0),
        total_ajustes: vendedor.ajustes,
        total_pagar: vendedor.total_pagar,
        resumo_financeiro: this.vendedorFinance(vendedor),
      };
    },
    rankingVendedores() {
      if (!this.resultado) return [];

      const realizadoEquipe = (this.resultado.vendedores || []).reduce(
        (total, vendedor) => total + Number(vendedor.realizado_individual || 0),
        0
      );
      return [...(this.resultado.vendedores || [])]
        .map((vendedor) => ({
          ...vendedor,
          participacao_equipe:
            realizadoEquipe > 0
              ? (Number(vendedor.realizado_individual || 0) / realizadoEquipe) *
                100
              : 0,
        }))
        .sort(
          (primeiro, segundo) =>
            Number(segundo.realizado_individual || 0) -
            Number(primeiro.realizado_individual || 0)
        );
    },
    overviewCards() {
      if (!this.resultado) return [];

      const equipe = this.resumoVisivel || {};
      const financeiro = equipe.resumo_financeiro || {};
      const vendedoresComMeta = this.rankingVendedores.filter(
        (vendedor) => Number(vendedor.meta_individual || 0) > 0
      );
      const metasAtingidas = vendedoresComMeta.filter(
        (vendedor) => Number(vendedor.atingimento_individual || 0) >= 100
      ).length;
      const semMetaEquipe =
        equipe.meta === null ||
        equipe.meta === undefined ||
        Number(equipe.meta) <= 0;

      return [
        {
          titulo: "Base comissionável",
          valor: this.money(equipe.realizado),
          legenda: "Vendido no período",
          icone: "cash-stack",
          classe: "bg-light-primary text-primary",
        },
        {
          titulo: this.periodo.id_vendedor
            ? "Meta individual"
            : "Meta da equipe",
          valor: semMetaEquipe ? "Sem meta" : this.money(equipe.meta),
          legenda: semMetaEquipe
            ? "Meta global não configurada"
            : this.percent(equipe.atingimento) + " de atingimento",
          icone: "bullseye",
          classe: "bg-light-success text-success",
        },
        {
          titulo: this.resultadoPorRecebimento
            ? "Comissão disponível"
            : "Comissão prevista",
          valor: this.money(
            this.resultadoPorRecebimento
              ? financeiro.valor_disponivel
              : equipe.total_pagar
          ),
          legenda: this.resultadoPorRecebimento
            ? this.money(financeiro.valor_aguardando_recebimento) +
              " aguardando recebimento"
            : this.money(equipe.total_comissao_base) + " de comissão base",
          icone: "wallet2",
          classe: "bg-light-info text-info",
        },
        {
          titulo: "Metas individuais",
          valor:
            vendedoresComMeta.length > 0
              ? metasAtingidas + " de " + vendedoresComMeta.length
              : "Sem metas",
          legenda: this.rankingVendedores.length + " vendedor(es) na análise",
          icone: "award",
          classe: "bg-light-warning text-warning",
        },
      ];
    },
    periodoRapidoAtivo() {
      const atual = {
        data_inicial: this.periodo.data_inicial,
        data_final: this.periodo.data_final,
      };
      const encontrado = this.periodShortcuts.find((atalho) => {
        const intervalo = this.datasPeriodoRapido(atalho.value);
        return (
          intervalo.data_inicial === atual.data_inicial &&
          intervalo.data_final === atual.data_final
        );
      });

      return encontrado ? encontrado.value : null;
    },
    historicoPeriodos() {
      return this.historico && Array.isArray(this.historico.periodos)
        ? this.historico.periodos
        : [];
    },
    historicoLabels() {
      return this.historicoPeriodos.map((periodo) =>
        this.rotuloPeriodoHistorico(periodo)
      );
    },
    temHistorico() {
      return this.historicoPeriodos.some(
        (periodo) =>
          Number(periodo.realizado || 0) !== 0 ||
          Number(
            this.resultadoPorRecebimento
              ? periodo.valor_disponivel
              : periodo.total_pagar
          ) !== 0
      );
    },
    seriesGraficoEvolucao() {
      return [
        {
          name: "Base comissionável",
          type: "column",
          data: this.historicoPeriodos.map((periodo) =>
            Number(periodo.realizado || 0)
          ),
        },
        {
          name: this.resultadoPorRecebimento
            ? "Comissão disponível"
            : "Comissão prevista",
          type: "line",
          data: this.historicoPeriodos.map((periodo) =>
            Number(
              this.resultadoPorRecebimento
                ? periodo.valor_disponivel || 0
                : periodo.total_pagar || 0
            )
          ),
        },
      ];
    },
    opcoesGraficoEvolucao() {
      return {
        chart: {
          type: "line",
          toolbar: { show: true },
          zoom: { enabled: true },
        },
        colors: ["#3699ff", this.primaryBrandColor],
        stroke: { width: [0, 3], curve: "smooth" },
        dataLabels: { enabled: false },
        xaxis: { categories: this.historicoLabels },
        yaxis: [
          {
            title: { text: "Base comissionável" },
            labels: { formatter: (valor) => this.moneyCompact(valor) },
          },
          {
            opposite: true,
            title: { text: "Comissão" },
            labels: { formatter: (valor) => this.moneyCompact(valor) },
          },
        ],
        tooltip: {
          y: [
            { formatter: (valor) => this.money(valor) },
            { formatter: (valor) => this.money(valor) },
          ],
        },
        legend: { position: "top" },
      };
    },
    seriesGraficoModalidades() {
      return [
        {
          name: "À vista",
          data: this.historicoPeriodos.map((periodo) =>
            Number(periodo.total_vista || 0)
          ),
        },
        {
          name: "A prazo",
          data: this.historicoPeriodos.map((periodo) =>
            Number(periodo.total_prazo || 0)
          ),
        },
      ];
    },
    opcoesGraficoModalidades() {
      return {
        chart: {
          type: "bar",
          stacked: true,
          toolbar: { show: false },
        },
        colors: [this.primaryBrandColor, "#8950fc"],
        plotOptions: {
          bar: { borderRadius: 3, columnWidth: "58%" },
        },
        dataLabels: { enabled: false },
        xaxis: {
          categories: this.historicoLabels,
          labels: { rotate: -45 },
        },
        yaxis: {
          labels: { formatter: (valor) => this.moneyCompact(valor) },
        },
        tooltip: {
          y: { formatter: (valor) => this.money(valor) },
        },
        legend: { position: "top" },
      };
    },
    seriesGraficoDesempenho() {
      const series = [
        {
          name: "Realizado",
          data: this.rankingVendedores.map((vendedor) =>
            Number(vendedor.realizado_individual || 0)
          ),
        },
      ];
      if (
        this.rankingVendedores.some(
          (vendedor) => Number(vendedor.meta_individual || 0) > 0
        )
      ) {
        series.push({
          name: "Meta individual",
          data: this.rankingVendedores.map((vendedor) =>
            Number(vendedor.meta_individual || 0)
          ),
        });
      }

      return series;
    },
    opcoesGraficoDesempenho() {
      return {
        chart: {
          type: "bar",
          toolbar: { show: false },
        },
        plotOptions: { bar: { horizontal: true } },
        colors: [this.primaryBrandColor, "#e4e6ef"],
        dataLabels: { enabled: false },
        xaxis: {
          categories: this.rankingVendedores.map((vendedor) => vendedor.nome),
          labels: {
            formatter: (valor) => this.moneyCompact(valor),
          },
        },
        tooltip: {
          y: {
            formatter: (valor) => this.money(valor),
          },
        },
      };
    },
    seriesGraficoComposicao() {
      if (!this.resultado) return [];
      const equipe = this.resumoVisivel || {};

      return [
        Math.max(0, Number(equipe.total_comissao_base || 0)),
        Math.max(0, Number(equipe.total_bonus || 0)),
      ];
    },
    temComissaoComposicao() {
      return this.seriesGraficoComposicao.some((valor) => valor > 0);
    },
    opcoesGraficoComposicao() {
      return {
        labels: ["Comissão base", "Bônus"],
        colors: [this.primaryBrandColor, "#1bc5bd"],
        legend: { position: "bottom" },
        dataLabels: { enabled: false },
        stroke: { colors: ["#ffffff"] },
        tooltip: {
          y: {
            formatter: (valor) => this.money(valor),
          },
        },
        plotOptions: {
          pie: {
            donut: {
              size: "72%",
              labels: {
                show: true,
                name: { show: true },
                value: {
                  show: true,
                  formatter: (valor) => this.money(valor),
                },
                total: {
                  show: true,
                  label: "Potencial calculado",
                  formatter: () => this.money(this.resumoVisivel.total_pagar),
                },
              },
            },
          },
        },
      };
    },
  },
  methods: {
    ...mapActions(["setLoading"]),
    planoNovo() {
      const vendedores = this.vendedores || [];

      return {
        id: null,
        nome: "Configuração de comissões",
        status: "rascunho",
        inicio_vigencia: moment().startOf("month").format("YYYY-MM-DD"),
        fim_vigencia: null,
        periodicidade: "mensal",
        evento_competencia: "faturamento",
        comissao_vista: 0,
        comissao_prazo: 0,
        incluir_nfe: true,
        incluir_nfce: true,
        incluir_pedidos: true,
        incluir_os: true,
        incluir_contratos: false,
        meta_equipe: 0,
        bonus_equipe_tipo: null,
        bonus_equipe_valor: 0,
        bonus_equipe_cumulativo: true,
        id_plano_contas: null,
        vencimento_dias: 0,
        vendedores: vendedores.map((vendedor) => ({
          id_vendedor: vendedor.id_vendedor,
          ativo: true,
          comissao_vista: Number(vendedor.comissao_vista || 0),
          comissao_prazo: Number(vendedor.comissao_prazo || 0),
          meta_individual: 0,
          tabela_comissao_chave: null,
          bonus_individual_tipo: null,
          bonus_individual_valor: 0,
          participa_meta_equipe: true,
        })),
        faixas: [],
        tabelas_comissao: [],
        formas_pagamento: this.formasPagamento.map((forma) => ({
          id_forma_pagamento: forma.id,
          descricao: forma.descricao,
          modalidade: this.inferirModalidade(forma.descricao),
        })),
      };
    },
    normalizarPlano(plano) {
      const regrasVendedor = (plano.vendedores || []).reduce((mapa, regra) => {
        mapa[Number(regra.id_vendedor)] = regra;
        return mapa;
      }, {});
      const classificacoes = (plano.formas_pagamento || []).reduce(
        (mapa, forma) => {
          mapa[Number(forma.id_forma_pagamento)] = forma.modalidade;
          return mapa;
        },
        {}
      );
      const tabelasComissao = (plano.tabelas_comissao || []).map(
        (tabela, tabelaIndex) => {
          const chave = String(
            tabela.chave ||
              tabela.id ||
              "tabela-" + Date.now() + "-" + tabelaIndex
          );
          return {
            ...tabela,
            _key: chave,
            chave,
            nome: tabela.nome || "",
            descricao: tabela.descricao || "",
            vincular_percentuais: tabela.vincular_percentuais !== false,
            ativo: tabela.ativo !== false,
            faixas: this.organizarFaixasTabela(
              (tabela.faixas || []).map((faixa, faixaIndex) => ({
                ...faixa,
                _key: String(
                  faixa.id ||
                    "faixa-" + Date.now() + "-" + tabelaIndex + "-" + faixaIndex
                ),
                percentual_inicial: Number(faixa.percentual_inicial || 0),
                percentual_final: this.inputNullable(faixa.percentual_final),
                comissao_percentual: Number(
                  faixa.comissao_percentual || faixa.recompensa_valor || 0
                ),
                comissao_percentual_prazo: Number(
                  faixa.comissao_percentual_prazo ??
                    faixa.recompensa_valor_prazo ??
                    faixa.comissao_percentual ??
                    faixa.recompensa_valor ??
                    0
                ),
                ativo: faixa.ativo !== false,
              }))
            ),
          };
        }
      );

      return {
        ...plano,
        periodicidade: "mensal",
        evento_competencia: plano.evento_competencia || "faturamento",
        meta_equipe: Number(plano.meta_equipe || 0),
        bonus_equipe_valor: Number(plano.bonus_equipe_valor || 0),
        vencimento_dias: 0,
        vendedores: this.vendedores.map((vendedor) => {
          const regra = regrasVendedor[Number(vendedor.id_vendedor)];
          const temRegra = Boolean(regra);

          return {
            id_vendedor: vendedor.id_vendedor,
            ativo:
              !temRegra || regra.ativo === undefined || regra.ativo === null
                ? true
                : Boolean(regra.ativo),
            comissao_vista: temRegra
              ? this.inputNullable(regra.comissao_vista)
              : Number(vendedor.comissao_vista || 0),
            comissao_prazo: temRegra
              ? this.inputNullable(regra.comissao_prazo)
              : Number(vendedor.comissao_prazo || 0),
            meta_individual: Number((regra && regra.meta_individual) || 0),
            tabela_comissao_chave:
              regra && regra.id_tabela_comissao
                ? String(regra.id_tabela_comissao)
                : null,
            bonus_individual_tipo:
              (regra && regra.bonus_individual_tipo) || null,
            bonus_individual_valor: Number(
              (regra && regra.bonus_individual_valor) || 0
            ),
            participa_meta_equipe:
              !temRegra ||
              regra.participa_meta_equipe === undefined ||
              regra.participa_meta_equipe === null
                ? true
                : Boolean(regra.participa_meta_equipe),
          };
        }),
        faixas: [],
        tabelas_comissao: tabelasComissao,
        formas_pagamento: this.formasPagamento.map((forma) => ({
          id_forma_pagamento: forma.id,
          descricao: forma.descricao,
          modalidade:
            classificacoes[Number(forma.id)] ||
            this.inferirModalidade(forma.descricao),
        })),
      };
    },
    async carregarTudo() {
      this.loading = true;
      this.setLoading(true);
      try {
        const resultados = await Promise.allSettled([
          this.comTimeout(this.$http.post("/vendedor/get-vendedor")),
          this.comTimeout(this.getFormasPagamentos()),
          this.comTimeout(this.$http.get("/comissoes/configuracao")),
          this.comTimeout(this.$http.get("/comissoes/apuracoes")),
        ]);
        const [
          vendedoresResult,
          formasResult,
          configuracaoResult,
          apuracoesResult,
        ] = resultados;

        this.vendedores =
          vendedoresResult.status === "fulfilled"
            ? vendedoresResult.value.data || []
            : [];
        this.formasPagamento =
          formasResult.status === "fulfilled" &&
          Array.isArray(formasResult.value)
            ? formasResult.value
            : [];
        this.apuracoes =
          apuracoesResult.status === "fulfilled"
            ? apuracoesResult.value.data.data || []
            : [];

        const planoInicial =
          configuracaoResult.status === "fulfilled"
            ? configuracaoResult.value.data.data
            : null;

        this.plano = planoInicial
          ? this.normalizarPlano(planoInicial)
          : this.planoNovo();

        const falhasComissao = [configuracaoResult, apuracoesResult].filter(
          (resultado) => resultado.status === "rejected"
        );
        if (falhasComissao.length) {
          this.$toast.error(
            "A configuração local foi aberta, mas a API de comissões não respondeu. Recarregue a página após atualizar o backend."
          );
        }
      } catch (error) {
        this.plano = this.planoNovo();
        this.showError(error, "Não foi possível carregar as comissões.");
      } finally {
        this.loading = false;
        this.setLoading(false);
      }
    },
    comTimeout(promise, milliseconds = 10000) {
      return Promise.race([
        promise,
        new Promise((resolve, reject) => {
          setTimeout(
            () => reject(new Error("Tempo limite ao carregar comissões.")),
            milliseconds
          );
        }),
      ]);
    },
    payloadPlano() {
      return {
        nome: this.plano.nome,
        inicio_vigencia: this.plano.inicio_vigencia,
        fim_vigencia: this.plano.fim_vigencia || null,
        periodicidade: "mensal",
        evento_competencia: this.plano.evento_competencia || "faturamento",
        comissao_vista: Number(this.plano.comissao_vista || 0),
        comissao_prazo: Number(this.plano.comissao_prazo || 0),
        incluir_nfe: Boolean(this.plano.incluir_nfe),
        incluir_nfce: Boolean(this.plano.incluir_nfce),
        incluir_pedidos: Boolean(this.plano.incluir_pedidos),
        incluir_os: this.plano.incluir_os !== false,
        incluir_contratos: Boolean(this.plano.incluir_contratos),
        meta_equipe: this.nullableNumber(this.plano.meta_equipe),
        bonus_equipe_tipo: this.plano.bonus_equipe_tipo || null,
        bonus_equipe_valor: this.plano.bonus_equipe_tipo
          ? Number(this.plano.bonus_equipe_valor || 0)
          : null,
        bonus_equipe_cumulativo: true,
        id_plano_contas: this.plano.id_plano_contas || null,
        vencimento_dias: 0,
        vendedores: this.plano.vendedores.map((item) => ({
          id_vendedor: Number(item.id_vendedor),
          ativo: Boolean(item.ativo),
          comissao_vista: this.nullableNumber(item.comissao_vista),
          comissao_prazo: this.nullableNumber(item.comissao_prazo),
          meta_individual: this.nullableNumber(item.meta_individual),
          tabela_comissao_chave: item.tabela_comissao_chave || null,
          bonus_individual_tipo: null,
          bonus_individual_valor: null,
          participa_meta_equipe: Boolean(item.participa_meta_equipe),
        })),
        faixas: [],
        tabelas_comissao: this.plano.tabelas_comissao.map((tabela) => ({
          chave: tabela._key,
          nome: tabela.nome,
          descricao: tabela.descricao || null,
          vincular_percentuais: tabela.vincular_percentuais !== false,
          ativo: tabela.ativo !== false,
          faixas: tabela.faixas.map((faixa) => ({
            percentual_inicial: Number(faixa.percentual_inicial || 0),
            percentual_final: this.nullableNumber(faixa.percentual_final),
            comissao_percentual: Number(faixa.comissao_percentual || 0),
            comissao_percentual_prazo: Number(
              tabela.vincular_percentuais
                ? faixa.comissao_percentual || 0
                : faixa.comissao_percentual_prazo || 0
            ),
            ativo: faixa.ativo !== false,
          })),
        })),
        formas_pagamento: this.plano.formas_pagamento.map((forma) => ({
          id_forma_pagamento: Number(forma.id_forma_pagamento),
          modalidade: forma.modalidade,
        })),
      };
    },
    validarPlano() {
      if (!this.plano.inicio_vigencia) {
        this.$toast.error("Informe a data inicial da configuração.");
        return false;
      }
      if (
        !this.plano.incluir_nfe &&
        !this.plano.incluir_nfce &&
        !this.plano.incluir_pedidos &&
        !this.plano.incluir_os &&
        !this.plano.incluir_contratos
      ) {
        this.$toast.error("Selecione ao menos uma origem de vendas.");
        return false;
      }

      const nomes = new Set();
      for (const tabela of this.plano.tabelas_comissao) {
        tabela.faixas = this.organizarFaixasTabela(tabela.faixas);
        const nome = String(tabela.nome || "").trim();
        if (!nome) {
          this.$toast.error("Informe o nome de todas as tabelas de comissão.");
          return false;
        }
        if (nomes.has(nome.toLowerCase())) {
          this.$toast.error(
            "Use nomes diferentes para as tabelas de comissão."
          );
          return false;
        }
        nomes.add(nome.toLowerCase());
        if (!tabela.faixas.length) {
          this.$toast.error(`Adicione ao menos uma faixa à tabela “${nome}”.`);
          return false;
        }

        const faixaInvalida = tabela.faixas.find(
          (faixa) =>
            faixa.percentual_final !== null &&
            faixa.percentual_final !== "" &&
            Number(faixa.percentual_final) <= Number(faixa.percentual_inicial)
        );
        if (faixaInvalida) {
          this.$toast.error(
            `Revise os limites das faixas da tabela “${nome}”.`
          );
          return false;
        }
        const percentualInvalido = tabela.faixas.some((faixa) => {
          const vista = Number(faixa.comissao_percentual);
          const prazo = tabela.vincular_percentuais
            ? vista
            : Number(faixa.comissao_percentual_prazo);

          return (
            !Number.isFinite(vista) ||
            vista < 0 ||
            vista > 100 ||
            !Number.isFinite(prazo) ||
            prazo < 0 ||
            prazo > 100
          );
        });
        if (percentualInvalido) {
          this.$toast.error(
            `Informe percentuais entre 0% e 100% em todas as faixas de “${nome}”.`
          );
          return false;
        }
        const semLimiteAntesDaUltima = tabela.faixas.some(
          (faixa, index) =>
            index < tabela.faixas.length - 1 &&
            (faixa.percentual_final === null || faixa.percentual_final === "")
        );
        if (semLimiteAntesDaUltima) {
          this.$toast.error(
            `Somente a última faixa de “${nome}” pode ficar sem limite.`
          );
          return false;
        }
      }

      const vendedorSemMeta = this.plano.vendedores.find(
        (item) =>
          item.tabela_comissao_chave && Number(item.meta_individual || 0) <= 0
      );
      if (vendedorSemMeta) {
        this.$toast.error(
          `Informe a meta individual de ${this.vendedorNome(
            vendedorSemMeta.id_vendedor
          )} para usar a tabela progressiva.`
        );
        return false;
      }
      return true;
    },
    async salvarPlano() {
      if (!this.validarPlano()) return;
      this.saving = true;
      try {
        const response = await this.$http.put(
          "/comissoes/configuracao",
          this.payloadPlano()
        );
        this.plano = this.normalizarPlano(response.data.data);
        await this.simular(true);
        this.$toast.success("Configuração salva e aplicada.");
      } catch (error) {
        this.showError(error, "Não foi possível salvar a configuração.");
      } finally {
        this.saving = false;
      }
    },
    novaChave(prefixo) {
      return (
        prefixo +
        "-" +
        Date.now() +
        "-" +
        Math.random().toString(36).slice(2, 8)
      );
    },
    adicionarTabelaComissao() {
      this.plano.tabelas_comissao.push({
        _key: this.novaChave("tabela"),
        nome: "",
        descricao: "",
        vincular_percentuais: true,
        ativo: true,
        faixas: [
          {
            _key: this.novaChave("faixa"),
            percentual_inicial: 0,
            percentual_final: null,
            comissao_percentual: 0,
            comissao_percentual_prazo: 0,
            ativo: true,
          },
        ],
      });
    },
    removerTabelaComissao(index) {
      const tabela = this.plano.tabelas_comissao[index];
      if (!tabela) return;
      const quantidade = this.quantidadeVendedoresTabela(tabela._key);
      this.plano.vendedores.forEach((vendedor) => {
        if (vendedor.tabela_comissao_chave === tabela._key) {
          vendedor.tabela_comissao_chave = null;
        }
      });
      this.plano.tabelas_comissao.splice(index, 1);
      if (quantidade) {
        this.$toast.info(
          "A tabela foi removida e os vendedores voltaram à comissão padrão."
        );
      }
    },
    quantidadeVendedoresTabela(chave) {
      return this.plano.vendedores.filter(
        (vendedor) => vendedor.tabela_comissao_chave === chave
      ).length;
    },
    alternarVinculoPercentuais(tabela) {
      tabela.vincular_percentuais = !tabela.vincular_percentuais;
      if (tabela.vincular_percentuais) {
        tabela.faixas.forEach((faixa) => {
          faixa.comissao_percentual_prazo = Number(
            faixa.comissao_percentual || 0
          );
        });
      }
    },
    sincronizarPercentualPrazo(tabela, faixa) {
      if (!tabela.vincular_percentuais) return;
      faixa.comissao_percentual_prazo = Number(faixa.comissao_percentual || 0);
    },
    desfocarCampoNumerico(event) {
      if (event && event.currentTarget) {
        event.currentTarget.blur();
      }
    },
    percentualFaixaLabel(faixa, vinculada) {
      const vista = Number(faixa.comissao_percentual || 0);
      const prazo = vinculada
        ? vista
        : Number(faixa.comissao_percentual_prazo || 0);

      return vista === prazo
        ? `${this.percent(vista)} à vista e a prazo`
        : `Vista ${this.percent(vista)} · prazo ${this.percent(prazo)}`;
    },
    adicionarFaixaTabela(tabela) {
      const ultima = tabela.faixas[tabela.faixas.length - 1] || null;
      if (
        ultima &&
        (ultima.percentual_final === null || ultima.percentual_final === "")
      ) {
        this.$toast.warning(
          "Defina o limite da última faixa antes de criar a próxima."
        );
        return;
      }

      const inicio = ultima ? Number(ultima.percentual_final) + 1 : 0;
      tabela.faixas.push({
        _key: this.novaChave("faixa"),
        percentual_inicial: inicio,
        percentual_final: null,
        comissao_percentual: 0,
        comissao_percentual_prazo: 0,
        ativo: true,
      });
    },
    removerFaixaTabela(tabela, index) {
      tabela.faixas.splice(index, 1);
      tabela.faixas = this.organizarFaixasTabela(tabela.faixas);
    },
    onFaixaTabelaInicioAlterado(tabela, faixa) {
      faixa.percentual_inicial = Math.max(
        0,
        Number(faixa.percentual_inicial || 0)
      );
      if (
        faixa.percentual_final !== null &&
        faixa.percentual_final !== "" &&
        Number(faixa.percentual_final) <= Number(faixa.percentual_inicial)
      ) {
        faixa.percentual_final = Number(faixa.percentual_inicial) + 1;
      }
      tabela.faixas = this.organizarFaixasTabela(tabela.faixas);
    },
    onFaixaTabelaLimiteAlterado(tabela, faixa, index) {
      faixa.percentual_final = this.inputNullable(faixa.percentual_final);
      const posterior = tabela.faixas[index + 1] || null;

      if (faixa.percentual_final === null && posterior) {
        faixa.percentual_final = Math.max(
          Number(faixa.percentual_inicial || 0) + 1,
          Number(posterior.percentual_inicial || 0) - 1
        );
        this.$toast.warning("Somente a última faixa pode ficar sem limite.");
      }

      if (
        faixa.percentual_final !== null &&
        Number(faixa.percentual_final) <= Number(faixa.percentual_inicial)
      ) {
        faixa.percentual_final = Number(faixa.percentual_inicial) + 1;
      }
      tabela.faixas = this.organizarFaixasTabela(tabela.faixas);
    },
    organizarFaixasTabela(faixas) {
      let anterior = null;
      return faixas.map((faixa) => {
        if (
          anterior &&
          anterior.percentual_final !== null &&
          anterior.percentual_final !== ""
        ) {
          faixa.percentual_inicial = Number(anterior.percentual_final) + 1;
        }
        anterior = faixa;
        return faixa;
      });
    },
    temMetaIndividual(item) {
      return (
        Boolean(item.tabela_comissao_chave) ||
        Number(item.meta_individual || 0) > 0
      );
    },
    isMoneyBonusType(tipo) {
      return tipo === "fixo";
    },
    aplicarCompetenciaSelecionada() {
      if (!this.competenciaApuracao) return;
      const competencia = moment(
        this.competenciaApuracao + "-01",
        "YYYY-MM-DD"
      );
      this.periodo.data_inicial = competencia
        .clone()
        .startOf("month")
        .format("YYYY-MM-DD");
      this.periodo.data_final = competencia
        .clone()
        .endOf("month")
        .format("YYYY-MM-DD");
    },
    payloadAnalise() {
      const payload = this.periodoPayload();
      if (this.plano.id) {
        payload.id_plano = this.plano.id;
      }

      return payload;
    },
    validarPeriodoAnalise(silencioso = false) {
      if (!this.periodo.data_inicial || !this.periodo.data_final) {
        if (!silencioso) {
          this.$toast.warning("Informe o período completo.");
        }
        return false;
      }
      if (moment(this.periodo.data_inicial).isAfter(this.periodo.data_final)) {
        if (!silencioso) {
          this.$toast.warning(
            "A data inicial não pode ser maior que a data final."
          );
        }
        return false;
      }
      return true;
    },
    async simular(silencioso = false) {
      if (!this.validarPeriodoAnalise(silencioso)) return;

      this.processing = true;
      try {
        const payload = this.payloadAnalise();
        const [previewResponse, historicoResponse] = await Promise.all([
          this.$http.post("/comissoes/apuracoes/preview", payload),
          this.$http.post("/comissoes/apuracoes/historico", {
            ...payload,
            agrupamento: this.agrupamentoHistorico,
          }),
        ]);
        this.resultado = previewResponse.data.data;
        this.historico = historicoResponse.data.data;
      } catch (error) {
        if (!silencioso) {
          this.showError(error, "Não foi possível simular a comissão.");
        }
      } finally {
        this.processing = false;
      }
    },
    async alterarAgrupamentoHistorico(agrupamento) {
      this.agrupamentoHistorico = agrupamento;
      if (!this.validarPeriodoAnalise()) return;

      this.processing = true;
      try {
        const response = await this.$http.post(
          "/comissoes/apuracoes/historico",
          {
            ...this.payloadAnalise(),
            agrupamento,
          }
        );
        this.historico = response.data.data;
      } catch (error) {
        this.showError(
          error,
          "Não foi possível atualizar o histórico de comissões."
        );
      } finally {
        this.processing = false;
      }
    },
    datasPeriodoRapido(tipo) {
      const fim = moment().endOf("month");
      if (tipo === "90_dias") {
        return {
          data_inicial: moment().subtract(89, "days").format("YYYY-MM-DD"),
          data_final: moment().format("YYYY-MM-DD"),
        };
      }
      if (tipo === "6_meses") {
        return {
          data_inicial: fim
            .clone()
            .subtract(5, "months")
            .startOf("month")
            .format("YYYY-MM-DD"),
          data_final: fim.format("YYYY-MM-DD"),
        };
      }
      if (tipo === "12_meses") {
        return {
          data_inicial: fim
            .clone()
            .subtract(11, "months")
            .startOf("month")
            .format("YYYY-MM-DD"),
          data_final: fim.format("YYYY-MM-DD"),
        };
      }

      return {
        data_inicial: moment().startOf("month").format("YYYY-MM-DD"),
        data_final: moment().endOf("month").format("YYYY-MM-DD"),
      };
    },
    async aplicarPeriodoRapido(tipo) {
      const periodo = this.datasPeriodoRapido(tipo);
      this.periodo.data_inicial = periodo.data_inicial;
      this.periodo.data_final = periodo.data_final;
      this.agrupamentoHistorico =
        tipo === "6_meses" || tipo === "12_meses" ? "mes" : "auto";
      await this.simular();
    },
    rotuloPeriodoHistorico(periodo) {
      if (this.historico.agrupamento === "mes") {
        return moment(periodo.periodo).format("MMM/YY");
      }

      return (
        moment(periodo.data_inicial).format("DD/MM") +
        "–" +
        moment(periodo.data_final).format("DD/MM")
      );
    },
    async calcularApuracao() {
      this.aplicarCompetenciaSelecionada();
      const confirmacao = await this.$swal.fire({
        icon: "question",
        title: "Fechar esta competência?",
        text:
          "A competência mensal de " +
          moment(this.periodo.data_inicial).format("MM/YYYY") +
          " será gravada para conferência antes da aprovação.",
        showCancelButton: true,
        confirmButtonText: "Calcular e fechar",
        cancelButtonText: "Cancelar",
      });
      if (!confirmacao.isConfirmed) return;

      this.processing = true;
      try {
        const response = await this.$http.post(
          "/comissoes/apuracoes/calcular",
          this.periodoPayload(false)
        );
        this.definirApuracaoSelecionada(response.data.data);
        this.resultado = null;
        await this.recarregarApuracoes();
        this.$toast.success("Apuração calculada e gravada.");
      } catch (error) {
        this.showError(error, "Não foi possível fechar a apuração.");
      } finally {
        this.processing = false;
      }
    },
    async carregarApuracao(id) {
      this.processing = true;
      try {
        const response = await this.$http.get("/comissoes/apuracoes/" + id);
        this.definirApuracaoSelecionada(response.data.data);
      } catch (error) {
        this.showError(error, "Não foi possível carregar a apuração.");
      } finally {
        this.processing = false;
      }
    },
    async aprovarApuracao() {
      const confirmacao = await this.$swal.fire({
        icon: "warning",
        title: "Aprovar a apuração?",
        text: "Após a aprovação, valores e lançamentos ficarão protegidos contra alteração.",
        showCancelButton: true,
        confirmButtonText: "Aprovar",
        cancelButtonText: "Cancelar",
        confirmButtonColor: this.primaryBrandColor,
      });
      if (!confirmacao.isConfirmed) return;

      this.processing = true;
      try {
        const response = await this.$http.post(
          "/comissoes/apuracoes/" + this.apuracaoSelecionada.id + "/aprovar"
        );
        this.definirApuracaoSelecionada(response.data.data, false);
        await this.recarregarApuracoes();
        this.$toast.success("Apuração aprovada.");
      } catch (error) {
        this.showError(error, "Não foi possível aprovar a apuração.");
      } finally {
        this.processing = false;
      }
    },
    async cancelarApuracao() {
      const confirmacao = await this.$swal.fire({
        icon: "warning",
        title: "Cancelar esta apuração?",
        text: "A apuração será excluída e a competência poderá ser calculada novamente.",
        showCancelButton: true,
        confirmButtonText: "Cancelar apuração",
        cancelButtonText: "Voltar",
        confirmButtonColor: "#f64e60",
      });
      if (!confirmacao.isConfirmed) return;

      this.processing = true;
      try {
        await this.$http.post(
          "/comissoes/apuracoes/" + this.apuracaoSelecionada.id + "/cancelar"
        );
        this.apuracaoSelecionada = null;
        await this.recarregarApuracoes();
        this.$toast.success(
          "Apuração removida. A competência já pode ser calculada novamente."
        );
      } catch (error) {
        this.showError(error, "Não foi possível cancelar a apuração.");
      } finally {
        this.processing = false;
      }
    },
    async gerarFinanceiro() {
      const condicionado = this.apuracaoPorRecebimento;
      const confirmacao = await this.$swal.fire({
        icon: "question",
        title: condicionado
          ? "Gerar a comissão a pagar deste mês?"
          : "Gerar contas a pagar?",
        text: condicionado
          ? "O financeiro usará somente as baixas desta competência. Recebimentos de vendas antigas entram no pagamento, sem voltar para a meta ou para a comissão gerada do mês."
          : "Será criada uma conta por vendedor. Repetir esta operação não criará duplicidades.",
        input: "text",
        inputLabel: "Data de vencimento",
        inputValue: moment().format("YYYY-MM-DD"),
        inputAttributes: {
          required: true,
        },
        onOpen: (modal) => {
          const input = modal.querySelector(".swal2-input");
          if (input) input.type = "date";
        },
        showCancelButton: true,
        confirmButtonText: condicionado
          ? "Gerar valor a pagar"
          : "Gerar financeiro",
        cancelButtonText: "Cancelar",
        preConfirm: (dataVencimento) => {
          if (!dataVencimento) {
            this.$swal.showValidationMessage("Informe a data de vencimento.");
            return false;
          }

          return dataVencimento;
        },
      });
      if (!confirmacao.isConfirmed) return;

      this.processing = true;
      try {
        const response = await this.$http.post(
          "/comissoes/apuracoes/" +
            this.apuracaoSelecionada.id +
            "/gerar-financeiro",
          { data_vencimento: confirmacao.value }
        );
        this.definirApuracaoSelecionada(response.data.data, false);
        await this.recarregarApuracoes();
        this.$toast.success(
          condicionado
            ? "Comissão a pagar enviada ao financeiro sem duplicidades."
            : "Financeiro gerado sem duplicidades."
        );
      } catch (error) {
        this.showError(error, "Não foi possível gerar o financeiro.");
      } finally {
        this.processing = false;
      }
    },
    definirApuracaoSelecionada(apuracao, expandirPrimeiro = true) {
      this.apuracaoSelecionada = apuracao;
      if (
        expandirPrimeiro &&
        apuracao &&
        apuracao.vendedores &&
        apuracao.vendedores.length
      ) {
        this.sellerDetailsOpen = {
          [apuracao.vendedores[0].id]: true,
        };
      }
    },
    vendedorSnapshot(vendedor) {
      if (!vendedor) return {};
      if (vendedor.calculo_snapshot) return vendedor.calculo_snapshot;

      return {
        tabela_comissao: vendedor.tabela_comissao || null,
        faixa_comissao: vendedor.faixa_comissao || null,
      };
    },
    vendedorFuture(vendedor) {
      const vazio = {
        habilitado: false,
        quantidade: 0,
        valor_base: 0,
        comissao_estimada: null,
        estimativa_disponivel: false,
        proximo_vencimento: null,
        itens: [],
      };
      if (!vendedor) return vazio;

      return (
        vendedor.comissoes_futuras ||
        this.vendedorSnapshot(vendedor).comissoes_futuras ||
        vazio
      );
    },
    vendedorPayment(vendedor) {
      const vazio = {
        condicionado_recebimento: false,
        valor_gerado: Number((vendedor && vendedor.total_pagar) || 0),
        valor_a_pagar: 0,
        valor_liberado: 0,
        saldo_liberar: 0,
        quantidade: 0,
        itens: [],
      };
      if (!vendedor) return vazio;

      return {
        ...vazio,
        ...(vendedor.comissao_a_pagar || {}),
      };
    },
    vendedorFinance(vendedor) {
      const vazio = {
        condicionado_recebimento: false,
        valor_calculado: Number((vendedor && vendedor.total_pagar) || 0),
        valor_gerado: Number((vendedor && vendedor.total_pagar) || 0),
        valor_a_pagar: 0,
        valor_disponivel: 0,
        valor_aguardando_recebimento: 0,
        valor_liberado: 0,
        saldo_liberar: 0,
        saldo_total: Number((vendedor && vendedor.total_pagar) || 0),
      };
      if (!vendedor) return vazio;

      return {
        ...vazio,
        ...(vendedor.resumo_financeiro ||
          this.vendedorSnapshot(vendedor).resumo_financeiro ||
          {}),
      };
    },
    monthYear(value) {
      return value ? moment(value).format("MM/YYYY") : "—";
    },
    sellerFinanceStatusLabel(vendedor) {
      const labels = {
        gerado: "Financeiro concluído",
        parcial: "Liberação parcial",
        aguardando_recebimento: "Aguardando recebimento",
        pendente: "Financeiro pendente",
      };

      return labels[vendedor.status_financeiro] || "Financeiro pendente";
    },
    sellerFinanceStatusClass(vendedor) {
      const classes = {
        gerado: "label-light-success",
        parcial: "label-light-info",
        aguardando_recebimento: "label-light-warning",
        pendente: "label-light-primary",
      };

      return classes[vendedor.status_financeiro] || "label-light";
    },
    sellerAccountLabels(vendedor) {
      return (vendedor.liberacoes || [])
        .filter((item) => item.id_conta_pagar)
        .map((item) => "#" + item.id_conta_pagar)
        .join(", ");
    },
    vendedorRegraLabel(vendedor) {
      const tabela = this.vendedorSnapshot(vendedor).tabela_comissao;
      return tabela && tabela.nome
        ? tabela.nome
        : "Percentuais à vista e a prazo";
    },
    vendedorFaixaLabel(vendedor) {
      const snapshot = this.vendedorSnapshot(vendedor);
      const faixa = snapshot.faixa_comissao;
      if (!faixa) {
        return snapshot.tabela_comissao
          ? "Abaixo da primeira faixa"
          : "Regra padrão";
      }

      const limiteFinal =
        faixa.percentual_final === null || faixa.percentual_final === undefined
          ? "sem limite"
          : this.percent(faixa.percentual_final);
      return (
        this.percent(faixa.percentual_inicial) +
        " a " +
        limiteFinal +
        " · " +
        this.percentuaisFaixaSnapshot(faixa)
      );
    },
    vendedorPercentualLabel(vendedor) {
      const faixa = this.vendedorSnapshot(vendedor).faixa_comissao;
      if (faixa) return this.percentuaisFaixaSnapshot(faixa);

      return (
        "Vista " +
        this.percent(vendedor.comissao_vista_percentual) +
        " · prazo " +
        this.percent(vendedor.comissao_prazo_percentual)
      );
    },
    percentuaisFaixaSnapshot(faixa) {
      const vista = Number(
        faixa.comissao_percentual_vista ?? faixa.comissao_percentual ?? 0
      );
      const prazo = Number(faixa.comissao_percentual_prazo ?? vista);

      return vista === prazo
        ? this.percent(vista)
        : `Vista ${this.percent(vista)} · prazo ${this.percent(prazo)}`;
    },
    progressWidth(valor) {
      return Math.max(0, Math.min(100, Number(valor || 0))) + "%";
    },
    initials(nome) {
      return String(nome || "?")
        .trim()
        .split(/\s+/)
        .slice(0, 2)
        .map((parte) => parte.charAt(0).toUpperCase())
        .join("");
    },
    toggleSellerDetails(id) {
      this.$set(this.sellerDetailsOpen, id, !this.sellerDetailsVisible(id));
    },
    sellerDetailsVisible(id) {
      return Boolean(this.sellerDetailsOpen[id]);
    },
    origemLabel(origem) {
      const labels = {
        nfe: "NF-e",
        nfce: "NFC-e",
        pedido: "Pedido",
        os: "OS",
        contrato: "Contrato",
      };
      return labels[origem] || origem || "Ajuste";
    },
    modalidadeLabel(modalidade) {
      const labels = {
        vista: "À vista",
        prazo: "A prazo",
      };
      return labels[modalidade] || modalidade || "—";
    },
    async abrirPdfVendedor(vendedor) {
      const janela = window.open("", "_blank");
      this.pdfSellerId = vendedor.id_vendedor;
      try {
        const response = await this.$http.get(
          "/comissoes/apuracoes/" +
            this.apuracaoSelecionada.id +
            "/vendedores/" +
            vendedor.id_vendedor +
            "/pdf",
          { responseType: "blob" }
        );
        const arquivo = new Blob([response.data], {
          type: "application/pdf",
        });
        const url = window.URL.createObjectURL(arquivo);
        if (janela) {
          janela.location.href = url;
        } else {
          const link = document.createElement("a");
          link.href = url;
          link.download =
            "apuracao-" +
            this.apuracaoSelecionada.id +
            "-" +
            vendedor.vendedor_nome +
            ".pdf";
          link.click();
        }
        window.setTimeout(() => window.URL.revokeObjectURL(url), 60000);
      } catch (error) {
        if (janela) janela.close();
        this.showError(error, "Não foi possível gerar o PDF da apuração.");
      } finally {
        this.pdfSellerId = null;
      }
    },
    periodoPayload(comFiltroVendedor = true) {
      const payload = {
        data_inicial: this.periodo.data_inicial,
        data_final: this.periodo.data_final,
      };
      if (comFiltroVendedor && this.periodo.id_vendedor) {
        payload.id_vendedor = this.periodo.id_vendedor;
      }
      return payload;
    },
    async recarregarApuracoes() {
      const response = await this.$http.get("/comissoes/apuracoes");
      this.apuracoes = response.data.data || [];
    },
    vendedorNome(id) {
      const vendedor = this.vendedores.find(
        (item) => Number(item.id_vendedor) === Number(id)
      );
      return vendedor ? vendedor.nome : "Vendedor #" + id;
    },
    inferirModalidade(descricao) {
      const texto = String(descricao || "").toLowerCase();
      if (/nenhum|cortesia|sem pagamento/.test(texto)) return "ignorar";
      if (/vista|pix|dinheiro|débito|debito/.test(texto)) return "vista";
      return "prazo";
    },
    inputNullable(valor) {
      return valor === null || valor === undefined || valor === ""
        ? null
        : Number(valor);
    },
    nullableNumber(valor) {
      return valor === null || valor === undefined || valor === ""
        ? null
        : Number(valor);
    },
    bonusValueLabel(tipo) {
      if (tipo === "fixo") return "Valor por vendedor (R$)";
      if (tipo === "percentual_comissao") return "Percentual sobre a comissão";
      if (tipo === "pontos_percentuais") return "Pontos sobre as vendas";
      return "Valor do bônus";
    },
    statusLabel(status) {
      const labels = {
        rascunho: "Rascunho",
        ativo: "Ativo",
        inativo: "Encerrado",
        calculado: "Calculado",
        aprovado: "Aprovado",
        financeiro_gerado: "Financeiro gerado",
        pago: "Pago",
        cancelado: "Cancelado",
      };
      return labels[status] || status || "";
    },
    statusClass(status) {
      const classes = {
        rascunho: "label-light-warning",
        ativo: "label-light-success",
        inativo: "label-light-dark",
        calculado: "label-light-info",
        aprovado: "label-light-primary",
        financeiro_gerado: "label-light-success",
        pago: "label-light-success",
        cancelado: "label-light-danger",
      };
      return classes[status] || "label-light";
    },
    money(valor) {
      return Number(valor || 0).toLocaleString("pt-BR", {
        style: "currency",
        currency: "BRL",
      });
    },
    moneyCompact(valor) {
      return Number(valor || 0).toLocaleString("pt-BR", {
        style: "currency",
        currency: "BRL",
        notation: "compact",
        maximumFractionDigits: 1,
      });
    },
    moneyNullable(valor) {
      return valor === null || valor === undefined
        ? "Sem meta"
        : this.money(valor);
    },
    percent(valor) {
      return (
        Number(valor || 0).toLocaleString("pt-BR", {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        }) + "%"
      );
    },
    dateBr(data) {
      return data ? moment(data).format("DD/MM/YYYY") : "-";
    },
    showError(error, fallback) {
      const response = error && error.response ? error.response.data : null;
      const errors = response && response.errors ? response.errors : null;
      const firstError = errors
        ? Object.values(errors).reduce((all, item) => all.concat(item), [])[0]
        : null;
      this.$toast.error(
        firstError ||
          (response && (response.message || response.msg)) ||
          fallback
      );
    },
  },
  watch: {
    async tabIndex(value) {
      if (value !== 5) return;
      this.aplicarCompetenciaSelecionada();
      await this.simular(true);
    },
  },
  async mounted() {
    if (this.itsblocked) {
      this.$router.push({ name: "assinatura" });
      return;
    }
    await this.carregarTudo();
    await this.simular(true);
  },
};
